import React from "react";
import { newId } from "./lib/ids";
import { Icon, Portal, genToken } from "./registry";
import { ConcreteWidgetSettings } from "./concreteform";   // the concrete estimate form's embed card, under the Concrete lens
/* ============================================================
   Fence Works — Estimator Widget settings + embed code
   ============================================================ */
function widgetUrl(token) {
  const base = location.origin;
  return base + "/widget/" + (token || "demo");
}

/* ---- collapsible embed snippet ----
   The two floating loaders are ~35 lines of minified JS. Dumping that whole
   wall into the card buried every setting under it — on a phone it was most
   of a screen of code a contractor is never meant to read. Long snippets now
   clamp to a few lines behind a "Show the full snippet" disclosure; the Copy
   button above always copies the WHOLE thing, so nobody has to expand it to
   install anything. Short iframe embeds (a handful of lines) stay open. */
function EmbedCode({ code, collapsedLines = 5 }: any) {
  const lines = React.useMemo(() => String(code || "").split("\n").length, [code]);
  const [open, setOpen] = React.useState<any>(false);
  // only worth hiding when there's meaningfully more than the clamp shows
  if (lines <= collapsedLines + 2) return <pre className="embed-code">{code}</pre>;
  return (
    <div className="embed-wrap">
      <div className="embed-clip">
        {/* the full snippet is always in the DOM — collapsing is a visual clamp,
            so select-all and find-in-page still reach every line */}
        <pre className="embed-code" style={open ? undefined : { maxHeight: Math.round(collapsedLines * 17.25) + 24, overflow: "hidden" }}>{code}</pre>
        {!open && <span className="embed-fade" aria-hidden="true" />}
      </div>
      <button type="button" className="btn btn-ghost btn-sm embed-more" aria-expanded={!!open} onClick={() => setOpen(!open)}>
        <Icon name={open ? "chevdown" : "chevright"} size={13} />
        {open ? "Hide the code" : `Show the full snippet (${lines} lines)`}
      </button>
      {!open && <span className="faint embed-more-hint">Copy grabs all of it — you never need to read it.</span>}
    </div>
  );
}

/* referral attribution: customers share /widget/<token>?ref=<their hub token>;
   leads created from that visit carry referredBy so the referrer gets credit
   (and their reward) on the customer profile. Captured once per page load. */
const REFERRED_BY = (() => { try { return new URLSearchParams(location.search).get("ref") || undefined; } catch (e) { return undefined; } })();

function EstimatorWidgetSettings({ store, go, params }: any) {
  const s = store.settings || {};
  const w = s.widget || {};
  const setW = (patch) => store.setSettings({ widget: { ...w, ...patch } });
  const token = w.token || "demo";
  // sidebar deep-links (Website → Yard Preview Widget) scroll to their card
  React.useEffect(() => {
    const sec = params && params.section;
    if (!sec) return;
    const el = document.getElementById("ww-" + sec);
    if (el) { el.scrollIntoView({ behavior: "smooth", block: "start" }); el.classList.add("set-flash"); setTimeout(() => el.classList.remove("set-flash"), 1200); }
  }, [params && params.section]);
  const [copied, setCopied] = React.useState<any>(false);
  const [copiedChat, setCopiedChat] = React.useState<any>(false);
  const [copiedBook, setCopiedBook] = React.useState<any>(false);
  const [copiedLauncher, setCopiedLauncher] = React.useState<any>(false);
  const [preview, setPreview] = React.useState<any>(false);
  const [chatPreview, setChatPreview] = React.useState<any>(false);
  const [bookPreview, setBookPreview] = React.useState<any>(false);
  const [launcherPreview, setLauncherPreview] = React.useState<any>(false);
  const accent = w.accent || "#19e3c2";

  React.useEffect(() => { if (!w.token) setW({ token: genToken().slice(0, 12) }); }, []);
  // the embedded widget can't see the price book (costs never leave the app),
  // so the customer $/ft ranges it quotes from are stamped here — refreshed
  // from the products and pricing every time this page is open
  const ranges = React.useMemo(() => widgetPriceRanges(store.allProducts || window.VFP.PRODUCTS, s), [store.settings, store.allProducts]);
  const stamp = widgetRangeStamp(ranges);
  React.useEffect(() => {
    if (!w.token) return;   // wait for the token write above so the two don't race
    if (stamp !== JSON.stringify(w.priceRanges || {})) setW({ priceRanges: JSON.parse(stamp) });
  }, [stamp, w.token]);

  const url = widgetUrl(token);
  const embed = `<!-- Fence Works instant estimator -->
<iframe src="${url}"
  style="width:100%;max-width:520px;height:640px;border:0;border-radius:16px"
  title="Get a fence estimate" loading="lazy"></iframe>`;

  // Inline booking form — same widget, opened straight to the "Book a consult"
  // tab so visitors land on the request-a-quote form (not the estimator).
  const bookEmbed = `<!-- Fence Works online booking -->
<iframe src="${url}?tab=book"
  style="width:100%;max-width:520px;height:700px;border:0;border-radius:16px"
  title="Book a fence consultation" loading="lazy"></iframe>`;

  // Both floating snippets (quote launcher + chat bubble) share one stylesheet
  // and one stacking registry on the host page, so any mix of them coexists:
  // each takes the next corner slot, opening one closes the others, and the
  // panel chrome (dark card + spinner) lives on the HOST side so there's never
  // a white iframe flash. The iframe src is set on first open — visitors who
  // never touch the widget never download it.
  const embedCss = `.fwx-btn{position:fixed;right:20px;bottom:var(--fwb,20px);z-index:2147483001;display:inline-flex;align-items:center;justify-content:center;gap:8px;height:54px;padding:0 22px;border-radius:28px;border:0;background:var(--fwa,#19e3c2);color:#04201b;font:700 15px/1 system-ui,-apple-system,sans-serif;box-shadow:0 4px 12px rgba(3,7,12,.18),0 10px 28px rgba(3,7,12,.26);cursor:pointer;transition:transform .18s ease,box-shadow .18s ease}.fwx-btn:hover{transform:translateY(-2px);box-shadow:0 6px 16px rgba(3,7,12,.22),0 14px 36px rgba(3,7,12,.32)}.fwx-btn:active{transform:none}.fwx-btn:focus-visible{outline:3px solid rgba(255,255,255,.9);outline-offset:2px}.fwx-btn.rd{width:54px;padding:0;border-radius:50%;font-size:24px}.fwx-panel{position:fixed;right:20px;bottom:calc(var(--fwb,20px) + 66px);z-index:2147483000;width:min(var(--fww,400px),calc(100vw - 32px));height:min(var(--fwh,660px),calc(100vh - var(--fwb,20px) - 86px));height:min(var(--fwh,660px),calc(100dvh - var(--fwb,20px) - 86px));background:#090d14;border:1px solid rgba(255,255,255,.09);border-radius:18px;box-shadow:0 12px 28px rgba(3,7,12,.22),0 24px 64px rgba(3,7,12,.4);overflow:hidden;opacity:0;transform:translateY(14px) scale(.98);transform-origin:bottom right;visibility:hidden;pointer-events:none;transition:opacity .22s ease,transform .22s cubic-bezier(.2,.9,.3,1.15),visibility 0s linear .22s}.fwx-panel.on{opacity:1;transform:none;visibility:visible;pointer-events:auto;transition:opacity .22s ease,transform .22s cubic-bezier(.2,.9,.3,1.15)}.fwx-panel::before{content:'';position:absolute;inset:0;margin:auto;width:30px;height:30px;border-radius:50%;border:3px solid rgba(255,255,255,.14);border-top-color:var(--fwa,#19e3c2);animation:fwxspin .8s linear infinite}.fwx-panel.ld::before{content:none}.fwx-frame{position:relative;width:100%;height:100%;border:0;display:block;background:transparent}@keyframes fwxspin{to{transform:rotate(360deg)}}@media (max-width:640px){.fwx-panel{left:10px;right:10px;top:10px;width:auto;height:auto}}@media (prefers-reduced-motion:reduce){.fwx-btn,.fwx-panel{transition:none}}`;

  // Floating "Get a Quote" launcher: a corner pill that expands the booking
  // widget — one self-contained snippet. The iframe loads ?floating=1, which
  // renders ONLY the expanded panel (the pill below is the launcher).
  const launcherEmbed = `<!-- Fence Works "Get a Quote" launcher -->
<script>
(function(){var O=${JSON.stringify(location.origin)},T=${JSON.stringify(token)},A=${JSON.stringify(accent)};
if(document.getElementById('fw-launch-'+T))return;
var reg=window.__fwEmbeds=window.__fwEmbeds||[];var B=(20+reg.length*66)+'px';
if(!document.getElementById('fw-embed-css-v2')){var c=document.createElement('style');c.id='fw-embed-css-v2';
c.textContent="${embedCss}";document.head.appendChild(c);}
var p=document.createElement('div');p.className='fwx-panel';p.id='fw-launch-'+T;
p.setAttribute('role','dialog');p.setAttribute('aria-label','Get a fence quote');
p.style.setProperty('--fwb',B);p.style.setProperty('--fwa',A);
var f=document.createElement('iframe');f.className='fwx-frame';f.title='Get a fence quote';f.allow='clipboard-write';p.appendChild(f);
var b=document.createElement('button');b.type='button';b.className='fwx-btn';b.textContent='Get a Quote';
b.setAttribute('aria-label','Get a quote');b.setAttribute('aria-expanded','false');
b.style.setProperty('--fwb',B);b.style.setProperty('--fwa',A);
var open=false,started=false;
function load(){if(!started){started=true;f.src=O+'/widget/'+T+'?floating=1&tab=book';}}
function set(o){open=o;p.classList.toggle('on',o);b.classList.toggle('rd',o);
if(o)p.style.setProperty('--fwb',(20+(reg.length-1)*66)+'px');
if(o){b.innerHTML='<svg width="18" height="18" viewBox="0 0 20 20" aria-hidden="true"><path d="M5 5l10 10M15 5L5 15" stroke="currentColor" stroke-width="2.4" stroke-linecap="round"/></svg>';}
else{b.textContent='Get a Quote';}
b.setAttribute('aria-expanded',o?'true':'false');b.setAttribute('aria-label',o?'Close':'Get a quote');}
var me={close:function(){if(open)set(false);}};reg.push(me);
b.onclick=function(){if(!open){load();for(var i=0;i<reg.length;i++)if(reg[i]!==me)reg[i].close();}set(!open);};
b.onmouseenter=load;
f.addEventListener('load',function(){p.classList.add('ld');});
window.addEventListener('message',function(e){if(e.data==='fw-widget-close'&&e.origin===O&&e.source===f.contentWindow)me.close();});
document.addEventListener('keydown',function(e){if(e.key==='Escape'||e.keyCode===27)me.close();});
document.body.appendChild(p);document.body.appendChild(b);})();
</script>`;

  // A self-contained loader: drops a floating chat button + iframe panel on the
  // contractor's site (no hosted script needed — the whole thing is the snippet).
  const chatEmbed = `<!-- Fence Works AI chat -->
<script>
(function(){var O=${JSON.stringify(location.origin)},T=${JSON.stringify(token)},A=${JSON.stringify(accent)};
if(document.getElementById('fw-chat-'+T))return;
var reg=window.__fwEmbeds=window.__fwEmbeds||[];var B=(20+reg.length*66)+'px';
if(!document.getElementById('fw-embed-css-v2')){var c=document.createElement('style');c.id='fw-embed-css-v2';
c.textContent="${embedCss}";document.head.appendChild(c);}
var p=document.createElement('div');p.className='fwx-panel';p.id='fw-chat-'+T;
p.setAttribute('role','dialog');p.setAttribute('aria-label','Chat with us');
p.style.setProperty('--fwb',B);p.style.setProperty('--fwa',A);p.style.setProperty('--fww','380px');p.style.setProperty('--fwh','560px');
var f=document.createElement('iframe');f.className='fwx-frame';f.title='Chat with us';f.allow='clipboard-write';p.appendChild(f);
var b=document.createElement('button');b.type='button';b.className='fwx-btn rd';b.innerHTML='&#128172;';
b.setAttribute('aria-label','Chat with us');b.setAttribute('aria-expanded','false');
b.style.setProperty('--fwb',B);b.style.setProperty('--fwa',A);
var open=false,started=false;
function load(){if(!started){started=true;f.src=O+'/chat/'+T;}}
function set(o){open=o;p.classList.toggle('on',o);
if(o)p.style.setProperty('--fwb',(20+(reg.length-1)*66)+'px');
if(o){b.innerHTML='<svg width="18" height="18" viewBox="0 0 20 20" aria-hidden="true"><path d="M5 5l10 10M15 5L5 15" stroke="currentColor" stroke-width="2.4" stroke-linecap="round"/></svg>';}
else{b.innerHTML='&#128172;';}
b.setAttribute('aria-expanded',o?'true':'false');b.setAttribute('aria-label',o?'Close chat':'Chat with us');}
var me={close:function(){if(open)set(false);}};reg.push(me);
b.onclick=function(){if(!open){load();for(var i=0;i<reg.length;i++)if(reg[i]!==me)reg[i].close();}set(!open);};
b.onmouseenter=load;
f.addEventListener('load',function(){p.classList.add('ld');});
window.addEventListener('message',function(e){if(e.data==='fw-widget-close'&&e.origin===O&&e.source===f.contentWindow)me.close();});
document.addEventListener('keydown',function(e){if(e.key==='Escape'||e.keyCode===27)me.close();});
document.body.appendChild(p);document.body.appendChild(b);})();
</script>`;

  function copy() { navigator.clipboard && navigator.clipboard.writeText(embed); setCopied(true); setTimeout(() => setCopied(false), 1800); }
  function copyChatEmbed() { navigator.clipboard && navigator.clipboard.writeText(chatEmbed); setCopiedChat(true); setTimeout(() => setCopiedChat(false), 1800); }
  function copyBookEmbed() { navigator.clipboard && navigator.clipboard.writeText(bookEmbed); setCopiedBook(true); setTimeout(() => setCopiedBook(false), 1800); }
  function copyLauncherEmbed() { navigator.clipboard && navigator.clipboard.writeText(launcherEmbed); setCopiedLauncher(true); setTimeout(() => setCopiedLauncher(false), 1800); }

  return (
    <div className="page fade-up">
      {/* the concrete estimate form, when this is the Concrete lens (concreteform.tsx, plan §5.5) */}
      {store.lens === "concrete" && <ConcreteWidgetSettings store={store} />}
      <h1 className="display" style={{ fontSize: 28, margin: 0 }}>Estimator Widget</h1>
      <p className="muted" style={{ margin: "4px 0 20px" }}>Embed a lead-capturing instant-estimate form on your own website. New requests land in your pipeline as quotes to follow up.</p>

      <div className="widget-layout">
        <div style={{ display: "grid", gap: 16 }}>
          <div className="card pad">
            <div className="row gap-8 kicker kicker-teal" style={{ marginBottom: 12 }}><Icon name="bolt" size={14} />Widget Settings</div>
            <label className="toggle-row" style={{ marginBottom: 12 }} onClick={() => setW({ enabled: !(w.enabled !== false) })}>
              <span className={"sw" + (w.enabled !== false ? " on" : "")}><span className="knob" /></span>
              <span style={{ fontSize: 13.5, fontWeight: 600 }}>Widget is live</span>
            </label>
            <label className="toggle-row" style={{ marginBottom: 12 }} onClick={() => setW({ requireContact: !w.requireContact })}>
              <span className={"sw" + (w.requireContact ? " on" : "")}><span className="knob" /></span>
              <span style={{ fontSize: 13.5, fontWeight: 600 }}>Require contact info before showing the estimate</span>
              <span className="muted" style={{ fontSize: 11.5, marginLeft: "auto" }}>captures the lead first</span>
            </label>
            <div style={{ marginBottom: 12 }}>
              <label className="field-label">Headline</label>
              <input className="input" value={w.headline || ""} placeholder="Get an instant fence estimate" onChange={e => setW({ headline: e.target.value })} />
            </div>
            <div style={{ marginBottom: 12 }}>
              <label className="field-label">Lead-to email</label>
              <input className="input" value={w.notifyEmail || ""} placeholder="bids@yourco.com" onChange={e => setW({ notifyEmail: e.target.value })} />
            </div>
            <div>
              <label className="field-label">Accent color</label>
              <div className="row gap-8">
                {["#19e3c2", "#4dd4ff", "#E44D4D", "#f5c451", "#8b7cf6"].map(col =>
                  <button key={col} className={"crew-color" + (accent === col ? " on" : "")} style={{ background: col }} onClick={() => setW({ accent: col })} />)}
              </div>
            </div>
          </div>

          <div className="card pad" id="ww-prices">
            <div className="row gap-8 kicker kicker-teal" style={{ marginBottom: 6 }}><Icon name="dollar" size={14} />Instant-estimate prices</div>
            <p className="muted" style={{ fontSize: 12, margin: "0 0 10px" }}>Each look is priced from your own products — your customer price per foot on a plain 150 ft run, at 5 ft, ±10%. A look you don't sell uses the industry-typical range. Homeowners see these $/ft figures only, never your costs or markup.</p>
            <div style={{ display: "grid", gap: 4 }}>
              {widgetStyles(ranges).map((st: any) => (
                <div key={st.id} className="row between" style={{ fontSize: 12.5, padding: "5px 0", borderTop: "1px solid var(--line)" }}>
                  <span style={{ fontWeight: 600 }}>{st.label}</span>
                  <span className="row gap-8"><span className="tnum" style={{ fontWeight: 700 }}>${st.lo}–${st.hi}/ft</span>
                    <span className="faint" style={{ fontSize: 11, minWidth: 110, textAlign: "right" }}>{st.fromBook ? `from ${ranges[st.id].n} product${ranges[st.id].n === 1 ? "" : "s"}` : "industry-typical"}</span></span>
                </div>
              ))}
            </div>
            <p className="faint" style={{ fontSize: 11, margin: "8px 0 0" }}>Your website's widget updates from your price book whenever this page is open — open it after you change product prices or markup.</p>
          </div>

          <div className="card pad">
            <div className="row between" style={{ marginBottom: 10 }}>
              <span className="kicker">Embed Code</span>
              <button className="btn btn-outline btn-sm" onClick={copy}>{copied ? <><Icon name="check" size={13} />Copied</> : <><Icon name="clipboard" size={13} />Copy</>}</button>
            </div>
            <EmbedCode code={embed} />
            <p className="faint" style={{ fontSize: 11.5, marginTop: 8 }}>Paste into any website (Squarespace, Wix, WordPress, raw HTML). The token in the URL routes leads to your account.</p>
            <button className="btn btn-ghost btn-sm" style={{ marginTop: 10 }} onClick={() => setPreview(true)}><Icon name="map" size={13} />Preview the live widget</button>
          </div>

          <div className="card pad">
            <div className="row between" style={{ marginBottom: 4 }}>
              <span className="kicker kicker-teal"><Icon name="bolt" size={13} /> AI Chat Bubble</span>
              <button className="btn btn-outline btn-sm" onClick={copyChatEmbed}>{copiedChat ? <><Icon name="check" size={13} />Copied</> : <><Icon name="clipboard" size={13} />Copy</>}</button>
            </div>
            <p className="muted" style={{ fontSize: 11.5, margin: "0 0 10px" }}>A floating chat bubble your visitors can open anytime — the AI concierge answers fence questions 24/7 and captures the lead. Drop this one snippet anywhere on your site.</p>
            <EmbedCode code={chatEmbed} />
            <p className="faint" style={{ fontSize: 11.5, marginTop: 8 }}>Same account token as the estimator. Replies use your <b>Website Chat</b> automation (AI Front Desk); turn it on there to make answers smart.</p>
            <button className="btn btn-ghost btn-sm" style={{ marginTop: 10 }} onClick={() => setChatPreview(true)}><Icon name="send" size={13} />Preview the chat bubble</button>
          </div>

          <div className="card pad">
            <div className="row between" style={{ marginBottom: 4 }}>
              <span className="kicker kicker-teal"><Icon name="calendar" size={13} /> Online Booking</span>
              <button className="btn btn-outline btn-sm" onClick={copyBookEmbed}>{copiedBook ? <><Icon name="check" size={13} />Copied</> : <><Icon name="clipboard" size={13} />Copy</>}</button>
            </div>
            <p className="muted" style={{ fontSize: 11.5, margin: "0 0 10px" }}>Let customers <b>book a consultation</b> right from your site — name, project basics, and a preferred date &amp; time. Each request lands in your pipeline as a quote to schedule. This embed opens straight to the booking tab.</p>
            <EmbedCode code={bookEmbed} />
            <p className="faint" style={{ fontSize: 11.5, marginTop: 8 }}>Same account token as the estimator — visitors can switch to the instant estimate from inside the widget too.</p>
            <button className="btn btn-ghost btn-sm" style={{ marginTop: 10 }} onClick={() => setBookPreview(true)}><Icon name="calendar" size={13} />Preview the booking form</button>
          </div>

          <VizWidgetCard w={w} setW={setW} url={url} />

          <div className="card pad">
            <div className="row between" style={{ marginBottom: 4 }}>
              <span className="kicker kicker-teal"><Icon name="bolt" size={13} /> "Get a Quote" Launcher</span>
              <button className="btn btn-outline btn-sm" onClick={copyLauncherEmbed}>{copiedLauncher ? <><Icon name="check" size={13} />Copied</> : <><Icon name="clipboard" size={13} />Copy</>}</button>
            </div>
            <p className="muted" style={{ fontSize: 11.5, margin: "0 0 10px" }}>A floating <b>Get a Quote</b> bubble that follows visitors in the corner and expands the booking widget — no page space needed. Drop this one snippet anywhere on your site.</p>
            <EmbedCode code={launcherEmbed} />
            <p className="faint" style={{ fontSize: 11.5, marginTop: 8 }}>Floating launcher · same lead capture as the inline embed. The widget only loads when a visitor opens it, so it never slows your site — and it stacks neatly with the chat bubble if you install both.</p>
            <button className="btn btn-ghost btn-sm" style={{ marginTop: 10 }} onClick={() => setLauncherPreview(true)}><Icon name="bolt" size={13} />Preview the launcher</button>
          </div>
        </div>

        <div className="card pad widget-preview-wrap">
          <span className="kicker" style={{ marginBottom: 10, display: "block" }}>Live Preview</span>
          <div className="widget-frame">
            <PublicEstimatorForm settings={store.settings} accent={accent} headline={w.headline} requireContact={w.requireContact} preview store={store} />
          </div>
        </div>
      </div>
      {preview && (
        <Portal>
        <div className="hub-preview-overlay" data-dialog onClick={() => setPreview(false)}>
          <div className="widget-standalone" style={{ minHeight: 0 }} onClick={e => e.stopPropagation()}>
            <PublicEstimatorForm settings={store.settings} accent={accent} headline={w.headline} requireContact={w.requireContact} preview store={store} />
          </div>
        </div>
        </Portal>
      )}
      {chatPreview && (
        <Portal>
        <div className="hub-preview-overlay" data-dialog onClick={() => setChatPreview(false)}>
          <div onClick={e => e.stopPropagation()} style={{ width: 380, maxWidth: "calc(100vw - 40px)", height: 560, maxHeight: "calc(100vh - 80px)", borderRadius: 16, overflow: "hidden", boxShadow: "0 12px 48px rgba(0,0,0,.4)" }}>
            <iframe src={`/chat/${token}`} title="Chat preview" style={{ width: "100%", height: "100%", border: 0, background: "#0d1117" }} />
          </div>
        </div>
        </Portal>
      )}
      {bookPreview && (
        <Portal>
        <div className="hub-preview-overlay" data-dialog onClick={() => setBookPreview(false)}>
          <div className="widget-standalone" style={{ minHeight: 0 }} onClick={e => e.stopPropagation()}>
            <PublicEstimatorForm settings={store.settings} accent={accent} headline={w.headline} defaultTab="book" requireContact={w.requireContact} preview store={store} />
          </div>
        </div>
        </Portal>
      )}
      {launcherPreview && (
        <Portal>
        <div className="hub-preview-overlay" data-dialog onClick={() => setLauncherPreview(false)}>
          <div onClick={e => e.stopPropagation()} style={{ textAlign: "center", color: "var(--fg-2)", pointerEvents: "none" }}>
            <Icon name="bolt" size={26} style={{ opacity: .6 }} />
            <p style={{ fontSize: 13.5, marginTop: 8 }}>This is how the floating launcher appears in the corner of your site.<br />Click the bubble (bottom-right) to expand it. Tap anywhere to close this preview.</p>
          </div>
          {/* the launcher positions itself fixed at the corner, just like on a real site */}
          <span onClick={e => e.stopPropagation()}>
            <FloatingWidget settings={store.settings} accent={accent} headline={w.headline} defaultTab="book" requireContact={w.requireContact} preview store={store} startOpen />
          </span>
        </div>
        </Portal>
      )}
    </div>
  );
}

/* ---- settings card for the "See it on my yard" tab: on/off + embed.
   The preview is a real 3D fence drawn in the VISITOR's browser
   (viz3d/YardPreview.tsx) — nothing renders on our side, so there's no
   quota, no credits and nothing to cap. ---- */
function VizWidgetCard({ w, setW, url }: any) {
  const viz = w.visualizer || {};
  const on = !!viz.enabled;
  const [copied, setCopied] = React.useState<any>(false);
  const setViz = (patch: any) => setW({ visualizer: { ...viz, enabled: on, ...patch } });
  const embed = `<!-- Fence Works "See it on my yard" 3D preview -->
<iframe src="${url}?tab=visual"
  style="width:100%;max-width:520px;height:880px;border:0;border-radius:16px"
  title="See a fence on your yard" loading="lazy"></iframe>`;
  return (
    <div className="card pad" id="ww-visual">
      <div className="row between" style={{ marginBottom: 4 }}>
        <span className="kicker kicker-teal"><Icon name="image" size={13} /> 3D Yard Preview</span>
        <button className="btn btn-outline btn-sm" onClick={() => { navigator.clipboard && navigator.clipboard.writeText(embed); setCopied(true); setTimeout(() => setCopied(false), 1800); }}>
          {copied ? <><Icon name="check" size={13} />Copied</> : <><Icon name="clipboard" size={13} />Copy</>}</button>
      </div>
      <p className="muted" style={{ fontSize: 11.5, margin: "0 0 10px" }}>Visitors snap a photo of their yard, tap where the fence would go, and <b>see a real 3D fence standing on their own photo</b> — every style, color and height, instantly. Then they request the real quote. The strongest "wow" a fence site can have.</p>
      <label className="toggle-row" style={{ marginBottom: 10 }} onClick={() => setViz({ enabled: !on })}>
        <span className={"sw" + (on ? " on" : "")}><span className="knob" /></span>
        <span style={{ fontSize: 13.5, fontWeight: 600 }}>Yard previews are live on my widget</span>
      </label>
      <EmbedCode code={embed} />
      <p className="faint" style={{ fontSize: 11.5, marginTop: 8 }}>Previews are drawn in the visitor's own browser — no credits, no daily limit, and their photo is never uploaded. The "See it on my yard" tab also appears in every other embed while this is on.</p>
    </div>
  );
}

/* ---- the actual embeddable estimator form (also used standalone) ---- */
/* Fence line-art rendered from the shipped SVG files (web/public/fence-*.svg)
   via CSS mask — one hosted asset per style/height that recolors with the
   widget accent on selection. Same crisp art in the picker, the height scale,
   and anywhere else, with nothing inline to maintain. */
function FenceGlyph({ src, className = "", style }: any) {
  return <span aria-hidden="true" className={"fence-glyph " + className}
    style={{ WebkitMaskImage: `url("${src}")`, maskImage: `url("${src}")`, ...style }} />;
}

/* the shared SMS opt-in checkbox — used by both the estimate and booking flows.
   A2P-compliant: unchecked by default, explicit description, frequency + rates
   disclosure, HELP/STOP, policy links. Automated texts only go to opted-in leads. */
/* Public lead capture: POST the lead and report whether it LANDED. A widget
   used to fire-and-forget here and show the thank-you regardless — a 5xx or a
   dropped connection meant the visitor left believing the contractor had their
   number. Now: r.ok is the only success; when fetch itself throws (network
   down, page unloading) a sendBeacon of the same JSON is the backstop and
   counts as queued. Callers keep the form data and offer a retry on false. */
async function postLead(url: string, payload: any): Promise<boolean> {
  const json = JSON.stringify(payload);
  try {
    const r = await fetch(url, { method: "POST", headers: { "Content-Type": "application/json" }, body: json });
    return r.ok;
  } catch (e) {
    try {
      if (navigator.sendBeacon) return navigator.sendBeacon(url, new Blob([json], { type: "application/json" }));
    } catch (e2) {}
    return false;
  }
}
/* inline "didn't send" line + retry, shared by the three public forms */
function LeadSendError({ onRetry, sending, accent }: any) {
  return (
    <div role="alert" style={{ marginTop: 8, padding: "8px 10px", borderRadius: 8, background: "rgba(245,158,11,.12)", border: "1px solid rgba(245,158,11,.4)", fontSize: 12.5, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, flexWrap: "wrap" }}>
      <span>Couldn't send — check your connection.</span>
      <button type="button" className="pubest-btn" style={{ background: accent, margin: 0, padding: "6px 12px", fontSize: 12.5, width: "auto" }} disabled={sending} onClick={onRetry}>{sending ? "Sending…" : "Tap to retry"}</button>
    </div>
  );
}

function SmsConsent({ checked, onToggle, co, accent }: any) {
  return (
    <label className="row gap-8" style={{ alignItems: "flex-start", margin: "8px 0 0", cursor: "pointer" }} onClick={onToggle}>
      <span className={"cbox" + (checked ? " on" : "")} style={{ marginTop: 2, flexShrink: 0 }}>{checked ? <Icon name="check" size={11} /> : null}</span>
      <span className="faint" style={{ fontSize: 10.5, lineHeight: 1.5, textAlign: "left" }}>
        Yes — text me from <b>{co}</b> about my estimate and appointment. Message frequency varies; message &amp; data rates may apply. Reply HELP for help or STOP to opt out anytime. <a href="https://fenceworks.ai/terms" target="_blank" rel="noopener" style={{ color: accent }}>Terms</a> · <a href="https://fenceworks.ai/privacy" target="_blank" rel="noopener" style={{ color: accent }}>Privacy</a>
      </span>
    </label>
  );
}

/* ---- shell: contractor header + estimate/book tab toggle + footer ----
   Both form factors (inline iframe and the floating launcher) render this. The
   active tab is its own little flow component so each owns its own step state. */
function PublicEstimatorForm({ settings, accent = "#19e3c2", headline, preview, store, requireContact, onSubmitted, widgetToken, defaultTab, floating, onClose }: any) {
  const co = (settings && settings.companyName) || "Demo Contractor";
  const phone = (settings && settings.companyPhone) || "";
  const loc = [settings && settings.companyCity, settings && settings.companyState].filter(Boolean).join(", ");
  const vizOn = !!(settings && settings.widget && settings.widget.visualizer && settings.widget.visualizer.enabled);
  const [tab, setTab] = React.useState<any>(defaultTab === "book" ? "book" : (defaultTab === "visual" && vizOn) ? "visual" : "estimate");
  // "price this fence" hand-off from the yard preview → seeds the estimator tab
  const [estSeed, setEstSeed] = React.useState<any>(null);

  return (
    <div className={"pubest" + (floating ? " pubest-floating" : "")} style={{ ["--waccent" as any]: accent }}>
      {/* contractor header */}
      <div className="pe-contractor">
        <div className="pe-logo" style={{ color: accent, borderColor: accent + "55", background: accent + "18" }}>{co.charAt(0)}</div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 800, fontSize: 15 }}>{co}</div>
          <div className="muted" style={{ fontSize: 11.5 }}>{loc && <><Icon name="mappin" size={10} /> {loc} · </>}<Icon name="check" size={10} /> Licensed &amp; insured</div>
        </div>
        {floating
          ? <button className="pe-close" type="button" aria-label="Close" onClick={() => onClose && onClose()}><Icon name="x" size={15} /></button>
          : phone ? <a className="pe-phone" href={"tel:" + phone}><Icon name="phone" size={12} />{phone}</a> : null}
      </div>

      {/* mode toggle: instant estimate / see it on my yard / book a consult */}
      <div className="pe-tabs" role="tablist">
        <button role="tab" aria-selected={tab === "estimate"} className={"pe-tab" + (tab === "estimate" ? " on" : "")} style={tab === "estimate" ? { color: accent, borderColor: accent } : null} onClick={() => setTab("estimate")}>
          <Icon name="bolt" size={13} />Instant estimate
        </button>
        {vizOn && (
          <button role="tab" aria-selected={tab === "visual"} className={"pe-tab" + (tab === "visual" ? " on" : "")} style={tab === "visual" ? { color: accent, borderColor: accent } : null} onClick={() => setTab("visual")}>
            <Icon name="image" size={13} />See it on my yard
          </button>
        )}
        <button role="tab" aria-selected={tab === "book"} className={"pe-tab" + (tab === "book" ? " on" : "")} style={tab === "book" ? { color: accent, borderColor: accent } : null} onClick={() => setTab("book")}>
          <Icon name="calendar" size={13} />Book a consult
        </button>
      </div>

      {tab === "estimate"
        ? <EstimatorFlow settings={settings} accent={accent} store={store} preview={preview} widgetToken={widgetToken} onSubmitted={onSubmitted} onBook={() => setTab("book")} seed={estSeed} />
        : tab === "visual"
          ? <VisualizeFlow settings={settings} accent={accent} store={store} preview={preview} widgetToken={widgetToken} onSubmitted={onSubmitted}
              onEstimate={(seed: any) => { setEstSeed(seed); setTab("estimate"); }} onBook={() => setTab("book")} />
          : <BookingForm settings={settings} accent={accent} store={store} preview={preview} widgetToken={widgetToken} onSubmitted={onSubmitted} />}

      <div className="pubest-foot">
        Powered by Fence Works
        <div style={{ fontSize: 10, lineHeight: 1.5, marginTop: 6, opacity: 0.75, textAlign: "center" }}>
          We only text you about your estimate, with your consent. Msg &amp; data rates may apply; message frequency varies. Reply STOP to opt out, HELP for help. <a href="https://fenceworks.ai/terms" target="_blank" rel="noopener" style={{ color: "inherit", textDecoration: "underline" }}>Terms</a> · <a href="https://fenceworks.ai/privacy" target="_blank" rel="noopener" style={{ color: "inherit", textDecoration: "underline" }}>Privacy</a>
        </div>
      </div>
    </div>
  );
}

/* ---- instant-estimate ballpark: the contractor's own per-foot prices ----
   The five looks the widget offers, with the industry-typical $/ft each one
   falls back to. A look the price book covers is priced from it instead:
   every product of that look is run through the engine for a plain 150 ft
   stretch (no gates, corners, tear-out or permit), its PRE-TAX customer price
   per foot is brought to the widget's 5 ft baseline (the height chips scale
   it back up), and the range is the cheapest to dearest such product, ±10%.
   Only those customer-facing $/ft figures ever leave the app — stamped on
   settings.widget.priceRanges while the owner has the widget page open —
   never a cost, a markup or a margin. */
const WIDGET_STYLES = [
  { id: "solid", label: "Solid Privacy", lo: 25, hi: 40, art: "/fence-privacy.svg" }, { id: "semi", label: "Semi-Privacy", lo: 22, hi: 35, art: "/fence-semi-privacy.svg" },
  { id: "picket", label: "Picket", lo: 18, hi: 28, art: "/fence-picket.svg" }, { id: "ranch", label: "Ranch Rail", lo: 15, hi: 25, art: "/fence-post-rail.svg" }, { id: "lattice", label: "Lattice Top", lo: 28, hi: 45, art: "/fence-lattice.svg" },
];
const WIDGET_HEIGHT_MULT: any = { 3: 0.8, 4: 0.9, 5: 1, 6: 1.1, 8: 1.3 };
function widgetHeightMult(h: any) {
  const n = Number(h);
  if (!(n > 0)) return 1;
  return WIDGET_HEIGHT_MULT[n] != null ? WIDGET_HEIGHT_MULT[n] : Math.max(0.7, Math.min(1.5, 1 + (n - 5) * 0.1));
}
/* which of the widget's looks a product is — null when it's none of them
   (chain link, wire, security, temporary) */
function widgetStyleOf(p: any) {
  if (!p) return null;
  const ed = p._editor || {};
  const cat = String(ed.category || p.category || "").toLowerCase();
  const txt = [ed.style, p.style, p.name, p.sub].filter(Boolean).join(" ").toLowerCase();
  if (/chain|mesh|wire|barb|security|palisade|anti-climb|expanded metal|temporary|construction|crowd|industrial/.test(txt + " " + cat)) return null;
  if (/lattice/.test(txt)) return "lattice";
  if (/semi|shadow ?box|louver|slat/.test(txt)) return "semi";
  if (/privacy|board on board|board-on-board|stockade|tongue|stone|solid/.test(txt)) return "solid";
  if (/picket|spear|finial|ring top|ornamental|majestic|genesis/.test(txt) || /iron|aluminum/.test(cat)) return "picket";
  if (/rail|ranch/.test(txt) || /farm|ranch/.test(cat)) return "ranch";
  return null;
}
function widgetProductHeight(p: any) {
  const ed = (p && p._editor) || {};
  const h = Number(p && p.heightFt) || Number(ed.height) || 0;
  if (h > 0) return h;
  const m = /(\d+(?:\.\d+)?)\s*(?:ft|foot|feet|')/i.exec(String((p && p.name) || ""));
  return m ? Number(m[1]) : 5;
}
function widgetPriceRanges(products: any[], settings: any) {
  const VFP: any = (window as any).VFP;
  const by: any = {};
  (products || []).forEach((p: any) => {
    const style = widgetStyleOf(p);
    if (!style || !VFP || !VFP.computeQuote) return;
    let perFt = 0;
    try {
      const q = VFP.computeQuote({ linearFeet: 150, corners: 0, gates: 0, removal: "none", permit: false, travelMiles: 0 }, p, settings || {});
      const sub = Number(q && (q.subtotal != null ? q.subtotal : q.customerTotal)) || 0;
      perFt = q && q.LF > 0 ? sub / q.LF : 0;
    } catch (e) { perFt = 0; }
    perFt = perFt / widgetHeightMult(widgetProductHeight(p));
    if (!(perFt >= 1 && perFt < 1000)) return;   // an unpriced product (no costs yet) says nothing
    (by[style] || (by[style] = [])).push(perFt);
  });
  const out: any = {};
  Object.keys(by).forEach((k) => {
    const xs = by[k];
    out[k] = { lo: Math.floor(Math.min(...xs) * 0.9), hi: Math.ceil(Math.max(...xs) * 1.1), n: xs.length };
  });
  return out;
}
/* the ranges as stored on settings.widget.priceRanges ({ look: { lo, hi } },
   keys sorted) — JSON, so a caller can compare before writing */
function widgetRangeStamp(ranges: any): string {
  return JSON.stringify(Object.keys(ranges || {}).sort().reduce((m: any, k) => (m[k] = { lo: ranges[k].lo, hi: ranges[k].hi }, m), {}));
}
/* the widget's looks with the price book laid over the fallbacks */
function widgetStyles(ranges: any) {
  return WIDGET_STYLES.map((s) => {
    const r = ranges && ranges[s.id];
    return r && r.lo > 0 && r.hi >= r.lo ? { ...s, lo: r.lo, hi: r.hi, fromBook: true } : { ...s, fromBook: false };
  });
}

/* ---- the instant-estimate flow (the original widget steps) ---- */
function EstimatorFlow({ settings, accent, preview, store, requireContact, widgetToken, onSubmitted, onBook, seed }: any) {
  const products = window.VFP.PRODUCTS;
  const co = (settings && settings.companyName) || "Demo Contractor";
  const [step, setStep] = React.useState<any>(seed ? 1 : 0);   // hand-off from the yard preview lands on the style/footage step
  // in the app (preview) the price book is right here, so it's always current;
  // on a contractor's website it's the ranges stamped from it
  const ranges = React.useMemo(() => store ? widgetPriceRanges(store.allProducts || products, store.settings)
    : (settings && settings.widget && settings.widget.priceRanges) || null, [store && store.settings, store && store.customProducts, settings]);
  const STYLES = widgetStyles(ranges);
  // one-line "what does this height feel like" note under the scale illustration
  const HEIGHT_NOTE: any = { 3: "waist-high — gardens & front yards", 4: "chest-high — front yards & pets", 5: "shoulder-high — light privacy", 6: "full privacy — the backyard standard", 8: "maximum privacy & sound buffer" };
  const [form, setForm] = React.useState<any>({ name: "", email: "", phone: "", address: "", zip: "", style: (seed && seed.style) || "solid", height: (seed && seed.height) || 6, feet: 150, tearout: false, smsConsent: false });
  const [sending, setSending] = React.useState<any>(false);
  const [sendErr, setSendErr] = React.useState<any>(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const style = STYLES.find(s => s.id === form.style) || STYLES[0];
  const ft = +form.feet || 0;
  const hMult = { 3: 0.8, 4: 0.9, 5: 1, 6: 1.1, 8: 1.3 }[form.height] || 1;
  const base = ft * ((style.lo + style.hi) / 2) * hMult + (form.tearout ? 600 : 0);
  const lo = Math.round((base * 0.85) / 50) * 50, hi = Math.round((base * 1.25) / 50) * 50;
  const tier = (m) => `$${Math.round(ft * m.lo * hMult / 50) * 50 + (form.tearout ? 600 : 0)}–$${Math.round(ft * m.hi * hMult / 50) * 50 + (form.tearout ? 600 : 0)}`;

  async function submit() {
    if (sending) return;
    if (store) {
      const id = newId("q");
      store.addQuote({ id, customer: form.name || "Website lead", phone: form.phone, email: form.email, address: form.address || form.zip,
        lf: ft, total: Math.round((lo + hi) / 2), productName: style.label + " " + form.height + "ft", productId: products[0].id, status: "pending",
        createdAt: Date.now(), source: "widget", referredBy: REFERRED_BY, inputs: { linearFeet: ft, gates: 0, corners: 0 } });
      store.addActivity(`New website lead: ${form.name || "someone"} — est. $${lo}–$${hi}`);
    } else if (widgetToken) {
      // embedded on a contractor's site → capture the lead + fire speed-to-lead.
      // The thank-you only shows once the POST landed; otherwise the form stays
      // filled with an inline retry (same payload).
      const base = (window.VFP_API_BASE || "").replace(/\/$/, "");
      if (base) {
        setSending(true); setSendErr(false);
        const ok = await postLead(`${base}/public/widget/${encodeURIComponent(widgetToken)}/lead`,
          { name: form.name, phone: form.phone, email: form.email, address: form.address, zip: form.zip,
            style: form.style, height: form.height, feet: ft, tearout: form.tearout, estimateLow: lo, estimateHigh: hi, smsConsent: !!form.smsConsent, referredBy: REFERRED_BY || null });
        setSending(false);
        if (!ok) { setSendErr(true); return; }
      }
    }
    setStep(3); onSubmitted && onSubmitted();
  }

  // Abandoned-quote recovery: when embedded on a contractor's site, if a visitor
  // saw their estimate (step ≥ 2) and left a name + contact but never submitted,
  // beacon it on page-hide so the abandoned_quote automation (when ON) can follow
  // up. Fires at most once, and only when there's a way to reach them.
  const liveRef = React.useRef<any>({});
  liveRef.current = { form, step, lo, hi, ft };
  const beaconedRef = React.useRef<any>(false);
  React.useEffect(() => {
    if (!widgetToken) return;
    const apiBase = (window.VFP_API_BASE || "").replace(/\/$/, "");
    if (!apiBase) return;
    function beaconAbandon() {
      const s = liveRef.current || {}, f = s.form || {};
      if (beaconedRef.current || s.step < 2 || s.step >= 3) return;   // only mid-flow, after the estimate, pre-submit
      if (!f.email && !f.phone) return;                              // need a way to reach them
      beaconedRef.current = true;
      try {
        const body = JSON.stringify({ name: f.name, email: f.email, phone: f.phone, address: f.address, zip: f.zip,
          style: f.style, height: f.height, feet: s.ft, estimateLow: s.lo, estimateHigh: s.hi, smsConsent: !!f.smsConsent });
        navigator.sendBeacon(`${apiBase}/public/widget/${encodeURIComponent(widgetToken)}/abandoned`, new Blob([body], { type: "application/json" }));
      } catch (e) {}
    }
    function onVis() { if (document.visibilityState === "hidden") beaconAbandon(); }
    window.addEventListener("pagehide", beaconAbandon);
    document.addEventListener("visibilitychange", onVis);
    return () => { window.removeEventListener("pagehide", beaconAbandon); document.removeEventListener("visibilitychange", onVis); };
  }, [widgetToken]);

  const Progress = () => <div className="pe-prog">{[0, 1, 2].map(i => <span key={i} className={step > i ? "done" : step === i ? "on" : ""} style={step >= i ? { background: accent } : null} />)}</div>;

  return (
    <div className="pubest-body">
      <Progress />
      {step === 0 && (<>
        <h3 className="display" style={{ fontSize: 19, margin: "10px 0 2px" }}>📍 Your Property</h3>
        <p className="muted" style={{ fontSize: 12.5, margin: "0 0 14px" }}>Where will the fence be installed?</p>
        <label className="field-label">Street address <span className="faint">(optional)</span></label>
        <input className="input" value={form.address} onChange={e => set("address", e.target.value)} placeholder="123 Main St" style={{ marginBottom: 10 }} />
        <label className="field-label">Zip code</label>
        <input className="input tnum" value={form.zip} onChange={e => set("zip", e.target.value)} placeholder="12345" />
        {/* SMS opt-in shown on the FIRST screen so the A2P CTA is verifiable the
            instant the widget URL loads (the same optional, unchecked consent the
            contact step uses — bound to one shared smsConsent flag). */}
        <SmsConsent checked={form.smsConsent} onToggle={() => set("smsConsent", !form.smsConsent)} co={co} accent={accent} />
        <button className="pubest-btn" style={{ background: accent }} onClick={() => setStep(1)}>Continue →</button>
      </>)}

      {step === 1 && (<>
        <div className="pe-live"><div><span className="pe-live-dot" style={{ background: accent }} /><b style={{ color: accent, fontSize: 11, letterSpacing: ".08em" }}>LIVE ESTIMATE</b><div className="display" style={{ fontSize: 24 }}>${lo.toLocaleString()} – ${hi.toLocaleString()}</div></div><span className="muted" style={{ fontSize: 11, maxWidth: 120, textAlign: "right" }}>Tighten it: pick a style &amp; enter footage</span></div>
        <label className="field-label" style={{ marginTop: 14 }}>Fence Style</label>
        <div className="pe-styles">
          {STYLES.map(st => (
            <button key={st.id} className={"pe-style" + (form.style === st.id ? " on" : "")} style={form.style === st.id ? { borderColor: accent } : null} onClick={() => set("style", st.id)}>
              <FenceGlyph src={st.art} className="pe-style-art" />
              <div className="pe-style-name">{st.label}</div><div className="muted" style={{ fontSize: 10.5 }}>~${st.lo}–${st.hi}/ft</div>
            </button>
          ))}
        </div>
        <label className="field-label" style={{ marginTop: 12 }}>Fence Height</label>
        <div className="pe-heights">{[3, 4, 5, 6, 8].map(h => <button key={h} className={"pe-h" + (form.height === h ? " on" : "")} style={form.height === h ? { borderColor: accent, color: accent } : null} onClick={() => set("height", h)}>{h} ft</button>)}</div>
        {/* to-scale illustration of the selected height next to people — makes the
            choice tangible and shows off the style/height art the widget ships */}
        <div className="pe-scale">
          <FenceGlyph src={`/fence-${form.height}ft.svg`} className="pe-scale-art" />
          <div className="pe-scale-cap"><b>{form.height} ft</b> · {HEIGHT_NOTE[form.height] || "custom height"}</div>
        </div>
        <label className="field-label" style={{ marginTop: 12 }}>Approximate Linear Feet</label>
        <input className="input tnum" type="number" min="0" value={form.feet} onChange={e => set("feet", e.target.value)} />
        <p className="faint" style={{ fontSize: 11, margin: "6px 0 12px" }}>💡 An average backyard is 150–200 linear feet</p>
        <button className={"toggle-row" + (form.tearout ? " on" : "")} onClick={() => set("tearout", !form.tearout)} style={{ width: "100%" }}>
          <span className={"sw" + (form.tearout ? " on" : "")}><span className="knob" /></span>
          <span style={{ fontSize: 13, fontWeight: 600 }}>Remove existing fence</span><span className="muted" style={{ fontSize: 11.5, marginLeft: "auto" }}>+~$600</span>
        </button>
        <button className="pubest-btn" style={{ background: accent }} onClick={() => setStep(2)}>See My Estimate →</button>
        <button className="pubest-back" onClick={() => setStep(0)}>← Back</button>
      </>)}

      {step === 2 && (<>
        <div style={{ textAlign: "center" }}>
          <div style={{ width: 46, height: 46, margin: "4px auto 8px", borderRadius: "50%", display: "grid", placeItems: "center", background: accent + "22", color: accent }}><Icon name="check" size={22} /></div>
          <div style={{ fontWeight: 700 }}>Your Estimate</div><div className="muted" style={{ fontSize: 12 }}>Based on what you picked</div>
          <div className="display" style={{ fontSize: 30, color: accent, margin: "8px 0 2px" }}>${lo.toLocaleString()} – ${hi.toLocaleString()}</div>
          <div className="muted" style={{ fontSize: 12 }}>{style.label} · {form.height} ft · {ft} linear ft{form.tearout ? " · + tear-out" : ""}</div>
        </div>
        <div className="kicker" style={{ textAlign: "center", margin: "16px 0 8px" }}>Compare price points for this footage</div>
        <div className="pe-tiers">
          {[{ t: "Budget", m: STYLES[3] }, { t: "Recommended", m: STYLES[0], pop: true }, { t: "Premium", m: STYLES[4] }].map((x, i) => (
            <div key={i} className={"pe-tier" + (x.pop ? " pop" : "")} style={x.pop ? { borderColor: accent } : null}>
              {x.pop && <span className="pe-tier-badge" style={{ background: accent }}>MOST POPULAR</span>}
              <div className="muted" style={{ fontSize: 10, letterSpacing: ".06em" }}>{x.t.toUpperCase()}</div>
              <div style={{ fontWeight: 700, fontSize: 13 }}>{x.m.label}</div>
              <div className="tnum" style={{ fontWeight: 700, fontSize: 13, marginTop: 4 }}>{tier(x.m)}</div>
              <div className="faint" style={{ fontSize: 10 }}>{x.m.fromBook ? "Our pricing" : "Industry-typical"}</div>
            </div>
          ))}
        </div>
        <div className="pe-leadnote">Want this sent to you? <b style={{ color: accent }}>{co}</b> will follow up within 24 hours — no spam, ever.</div>
        <input className="input" placeholder="Full name" value={form.name} onChange={e => set("name", e.target.value)} style={{ marginBottom: 8 }} />
        <input className="input" placeholder="Email" value={form.email} onChange={e => set("email", e.target.value)} style={{ marginBottom: 8 }} />
        <input className="input" placeholder="Phone (optional)" value={form.phone} onChange={e => set("phone", e.target.value)} />
        <SmsConsent checked={form.smsConsent} onToggle={() => set("smsConsent", !form.smsConsent)} co={co} accent={accent} />
        <button className="pubest-btn" style={{ background: accent }} disabled={!form.name || !form.email || sending} onClick={submit}>{sending ? "Sending…" : "Send Me This Estimate ✨"}</button>
        {sendErr && <LeadSendError onRetry={submit} sending={sending} accent={accent} />}
        <button className="pe-altcta" type="button" onClick={() => onBook && onBook()}>Prefer to talk it through? <b style={{ color: accent }}>Book a free consult →</b></button>
        <button className="pubest-back" onClick={() => setStep(1)}>← Back</button>
        <p className="faint" style={{ fontSize: 10.5, marginTop: 10, lineHeight: 1.5 }}>Estimate excludes gates, permits &amp; removal of existing fence (unless tear-out selected). Approximate, based on {style.fromBook ? co + "'s own per-foot pricing for this style" : "typical local costs"}; final pricing depends on site conditions. A rep will follow up to schedule a free on-site consult.</p>
      </>)}

      {step === 3 && (
        <div style={{ textAlign: "center", padding: "30px 10px" }}>
          <div style={{ width: 52, height: 52, margin: "0 auto 14px", borderRadius: "50%", display: "grid", placeItems: "center", background: accent + "22", color: accent }}><Icon name="check" size={26} /></div>
          <h3 className="display" style={{ fontSize: 20, margin: "0 0 6px" }}>You're all set{form.name ? ", " + form.name.split(" ")[0] : ""}!</h3>
          <p className="muted" style={{ fontSize: 13 }}>{co} will reach out within 24 hours with your detailed quote and a free on-site measure.</p>
          {preview && <button className="pubest-back" onClick={() => setStep(0)}>↺ Reset preview</button>}
        </div>
      )}
    </div>
  );
}

/* ---- "See it on my yard" — the PUBLIC 3D fence preview -------------------
   Homeowner snaps their yard, taps along where the fence would stand, and a
   real 3D fence appears IN their photo — every look, instantly, drawn in
   their own browser (viz3d/YardPreview.tsx, lazy so three.js stays out of
   this bundle). No upload, no render queue, nothing to meter. Every path
   ends at the same place: leave your info → the lead lands in the pipeline
   tagged "saw the 3D yard preview", or jump to the estimator pre-seeded
   with the style they just fell in love with. ---- */
const YardPreviewLazy: any = React.lazy(() => import("./viz3d/YardPreview"));
function VisualizeFlow({ settings, accent, preview, store, widgetToken, onSubmitted, onEstimate, onBook }: any) {
  const co = (settings && settings.companyName) || "Demo Contractor";
  const [file, setFile] = React.useState<File | null>(null);
  const [phase, setPhase] = React.useState<any>("upload");   // upload | preview | form | sent
  const [choice, setChoice] = React.useState<any>(null);     // the look on screen (YardPreview reports it)
  const [form, setForm] = React.useState<any>({ name: "", email: "", phone: "", address: "", smsConsent: false });
  const [sending, setSending] = React.useState<any>(false);
  const [sendErr, setSendErr] = React.useState<any>(false);
  const setF = (k: any, v: any) => setForm((f: any) => ({ ...f, [k]: v }));
  const fileRef = React.useRef<any>(null);
  const apiBase = (window.VFP_API_BASE || "").replace(/\/$/, "");
  const look = choice ? choice.label : "fence";
  const desc = choice ? [choice.label, choice.colorLabel, choice.height + " ft"].filter(Boolean).join(" · ") : "";
  // warm the 3D engine while they're still finding a photo
  React.useEffect(() => { import("./viz3d/YardPreview").catch(() => {}); }, []);

  function pick(f: any) {
    if (!f) return;
    if (!/^image\//.test(f.type || "") && !/\.(jpe?g|png|webp|heic|heif)$/i.test(f.name || "")) return;
    setFile(f); setPhase("preview");
  }
  async function submitLead() {
    if (!form.name || (!form.email && !form.phone) || sending) return;
    if (store) {
      const id = newId("q");
      store.addQuote({ id, customer: form.name, phone: form.phone, email: form.email, address: form.address,
        lf: 0, total: 0, productName: (choice ? choice.label : "Fence") + " — yard preview", productId: window.VFP.PRODUCTS[0].id, status: "pending",
        createdAt: Date.now(), source: "widget", referredBy: REFERRED_BY, inputs: { linearFeet: 0, gates: 0, corners: 0 } });
      store.addActivity(`Yard-preview lead: ${form.name} — ${desc || "3D preview"}`);
    } else if (widgetToken && apiBase) {
      setSending(true); setSendErr(false);
      const ok = await postLead(`${apiBase}/public/widget/${encodeURIComponent(widgetToken)}/lead`,
        { name: form.name, phone: form.phone, email: form.email, address: form.address,
          style: (choice && choice.est) || undefined, height: choice ? choice.height : undefined, smsConsent: !!form.smsConsent,
          visualized: true, visualStyle: desc, referredBy: REFERRED_BY || null });
      setSending(false);
      if (!ok) { setSendErr(true); return; }   // keep the form; the visitor can retry
    }
    setPhase("sent"); onSubmitted && onSubmitted();
  }

  return (
    <div className="pubest-body">
      {/* one picker for every "photo" button — opened straight from the tap */}
      <input ref={fileRef} type="file" accept="image/*" style={{ display: "none" }} onChange={(e) => { pick(e.target.files && e.target.files[0]); e.currentTarget.value = ""; }} />
      {phase === "upload" && (<>
        <h3 className="display" style={{ fontSize: 19, margin: "10px 0 2px" }}>📸 See a fence on YOUR yard</h3>
        <p className="muted" style={{ fontSize: 12.5, margin: "0 0 14px" }}>Snap a photo of where the fence would go, tap along the ground where it would stand, and see it in 3D — try every style and color, instantly.</p>
        <button className="pubest-btn" style={{ background: accent }} onClick={() => fileRef.current && fileRef.current.click()}>Take / upload a photo →</button>
        <p className="faint" style={{ fontSize: 10.5, marginTop: 10, textAlign: "center" }}>Stand where you'd look at the fence from, with the ground it would stand on in view. Your photo stays on your device.</p>
      </>)}

      {file && (phase === "preview" || phase === "form") && (
        <div style={{ display: phase === "preview" ? undefined : "none" }}>
          <React.Suspense fallback={<div className="wy-loading"><span className="wy-spin" style={{ borderTopColor: accent }} />Loading the 3D preview…</div>}>
            <YardPreviewLazy file={file} accent={accent} co={co} onChoice={setChoice} onQuote={() => setPhase("form")}
              onEstimate={(seed: any) => onEstimate && onEstimate(seed)} onRestart={() => fileRef.current && fileRef.current.click()} />
          </React.Suspense>
        </div>
      )}

      {phase === "form" && (<>
        <h3 className="display" style={{ fontSize: 19, margin: "6px 0 2px" }}>Get your real quote</h3>
        <p className="muted" style={{ fontSize: 12.5, margin: "0 0 12px" }}>{co} will follow up with pricing for the {look.toLowerCase()} you previewed — usually within 24 hours.</p>
        <input className="input" placeholder="Full name" value={form.name} onChange={e => setF("name", e.target.value)} style={{ marginBottom: 8 }} autoComplete="name" />
        <input className="input" placeholder="Email" value={form.email} onChange={e => setF("email", e.target.value)} style={{ marginBottom: 8 }} inputMode="email" autoComplete="email" />
        <input className="input" placeholder="Phone (optional)" value={form.phone} onChange={e => setF("phone", e.target.value)} style={{ marginBottom: 8 }} inputMode="tel" autoComplete="tel" />
        <input className="input" placeholder="Property address (optional)" value={form.address} onChange={e => setF("address", e.target.value)} autoComplete="street-address" />
        <SmsConsent checked={form.smsConsent} onToggle={() => setF("smsConsent", !form.smsConsent)} co={co} accent={accent} />
        <button className="pubest-btn" style={{ background: accent }} disabled={!form.name || (!form.email && !form.phone) || sending} onClick={submitLead}>{sending ? "Sending…" : "Send my quote request ✨"}</button>
        {sendErr && <LeadSendError onRetry={submitLead} sending={sending} accent={accent} />}
        <button className="pe-altcta" type="button" onClick={() => onBook && onBook()}>Prefer to talk it through? <b style={{ color: accent }}>Book a free consult →</b></button>
        {file && <button className="pubest-back" onClick={() => setPhase("preview")}>← Back to my preview</button>}
      </>)}

      {phase === "sent" && (
        <div style={{ textAlign: "center", padding: "30px 10px" }}>
          <div style={{ width: 52, height: 52, margin: "0 auto 14px", borderRadius: "50%", display: "grid", placeItems: "center", background: accent + "22", color: accent }}><Icon name="check" size={26} /></div>
          <h3 className="display" style={{ fontSize: 20, margin: "0 0 6px" }}>On its way{form.name ? ", " + form.name.split(" ")[0] : ""}!</h3>
          <p className="muted" style={{ fontSize: 13 }}>{co} got your request{form.email ? <> and will email <b style={{ color: accent }}>{form.email}</b></> : ""} with real pricing for your {look.toLowerCase()}.</p>
          {preview && <button className="pubest-back" onClick={() => { setPhase("upload"); setFile(null); setChoice(null); }}>↺ Reset preview</button>}
        </div>
      )}
    </div>
  );
}

/* ---- the "Book a consult" flow — customer self-service online booking ----
   A request-a-quote / book-a-consultation form: contact + project basics +
   preferred date & time window + notes. On submit it lands in the contractor's
   pipeline as a PENDING quote (same addQuote path as the estimator, which also
   surfaces it in "Needs attention" / To-Do) AND logs a dated follow-up so the
   requested date shows up as something to schedule. Embedded (no in-app store)
   it posts the lead to /public/widget/:token/lead with type:"booking". */
function BookingForm({ settings, accent, preview, store, widgetToken, onSubmitted }: any) {
  const products = window.VFP.PRODUCTS;
  const co = (settings && settings.companyName) || "Demo Contractor";
  const FENCE_TYPES = ["Wood privacy", "Vinyl / PVC", "Chain link", "Aluminum / Ornamental", "Ranch rail", "Not sure yet"];
  const WINDOWS = ["Morning (8–12)", "Afternoon (12–4)", "Evening (after 4)", "Any time"];
  const [step, setStep] = React.useState<any>(0);
  const [submitting, setSubmitting] = React.useState<any>(false);
  const [sendErr, setSendErr] = React.useState<any>(false);
  const [form, setForm] = React.useState<any>({ name: "", email: "", phone: "", address: "", fenceType: FENCE_TYPES[0], feet: "", date: "", window: WINDOWS[3], notes: "", smsConsent: false });
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  // earliest selectable date = tomorrow (no same-day consults)
  const minDate = React.useMemo(() => { const d = new Date(); d.setDate(d.getDate() + 1); return d.toISOString().slice(0, 10); }, []);
  const prettyDate = (s) => { if (!s) return ""; const d = new Date(s + "T00:00:00"); return isNaN(d.getTime()) ? s : d.toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" }); };
  const canSubmit = !!(form.name && (form.email || form.phone) && !submitting);

  async function submit() {
    if (!form.name || (!form.email && !form.phone) || submitting) return;
    setSubmitting(true); setSendErr(false);
    const ft = +form.feet || 0;
    const summary = `${form.fenceType}${ft ? " · ~" + ft + " ft" : ""}`;
    if (store) {
      const id = newId("q");
      // Land it in the pipeline as a pending quote. A pending quote already shows
      // in "Sent Quotes" + the "Needs attention"/To-Do feed; the booking sub-doc
      // carries the requested date so To-Do dates it as something to schedule
      // (see buildTodos). Notes go on the quote so they're not lost.
      store.addQuote({ id, customer: form.name, phone: form.phone, email: form.email, address: form.address,
        lf: ft, total: 0, productName: form.fenceType + " — consult", productId: products[0].id, status: "pending",
        createdAt: Date.now(), source: "booking", referredBy: REFERRED_BY, notes: form.notes || "",
        booking: { fenceType: form.fenceType, feet: ft, requestedDate: form.date || null, requestedWindow: form.window || null, notes: form.notes || "", smsConsent: !!form.smsConsent },
        // the quote document prints q.inputs.quoteNotes — carry the notes there too
        inputs: { linearFeet: ft, gates: 0, corners: 0, ...(form.notes ? { quoteNotes: form.notes } : {}) } });
      store.addActivity(`New consult request: ${form.name} — ${summary}${form.date ? " · " + prettyDate(form.date) : ""}`);
    } else if (widgetToken) {
      // embedded on a contractor's site → same lead endpoint as the estimator,
      // tagged type:"booking" with the requested date so the back end can route
      // it to the schedule + speed-to-lead.
      const base = (window.VFP_API_BASE || "").replace(/\/$/, "");
      if (base) {
        const ok = await postLead(`${base}/public/widget/${encodeURIComponent(widgetToken)}/lead`,
          { type: "booking", name: form.name, phone: form.phone, email: form.email, address: form.address,
            fenceType: form.fenceType, feet: ft, requestedDate: form.date || null, requestedWindow: form.window || null,
            notes: form.notes || "", smsConsent: !!form.smsConsent });
        if (!ok) { setSubmitting(false); setSendErr(true); return; }   // keep the form; inline retry re-POSTs the same payload
      }
    }
    setStep(1); onSubmitted && onSubmitted();
  }

  if (step === 1) {
    const first = form.name ? form.name.split(" ")[0] : "";
    return (
      <div className="pubest-body">
        <div style={{ textAlign: "center", padding: "24px 6px 10px" }}>
          <div style={{ width: 52, height: 52, margin: "0 auto 14px", borderRadius: "50%", display: "grid", placeItems: "center", background: accent + "22", color: accent }}><Icon name="check" size={26} /></div>
          <h3 className="display" style={{ fontSize: 20, margin: "0 0 6px" }}>Request received{first ? ", " + first : ""}!</h3>
          <p className="muted" style={{ fontSize: 13, margin: "0 0 4px" }}>
            {co} will confirm your free consultation{form.date ? <> for <b style={{ color: accent }}>{prettyDate(form.date)}</b>{form.window && form.window !== "Any time" ? " · " + form.window : ""}</> : " shortly"} and reach out within 24 hours.
          </p>
        </div>
        {/* portal hand-off */}
        <div className="pe-leadnote" style={{ textAlign: "center", marginTop: 4 }}>
          <Icon name="lock" size={12} style={{ verticalAlign: "-1px" }} /> <b>Track your project online.</b><br />
          We'll {form.email ? <>email <b style={{ color: accent }}>{form.email}</b></> : "text you"} a private link to your customer portal — view your quote, schedule and pay, all in one place.
        </div>
        {preview && <button className="pubest-back" onClick={() => { setForm({ name: "", email: "", phone: "", address: "", fenceType: FENCE_TYPES[0], feet: "", date: "", window: WINDOWS[3], notes: "", smsConsent: false }); setStep(0); setSubmitting(false); }}>↺ Reset preview</button>}
      </div>
    );
  }

  return (
    <div className="pubest-body">
      <h3 className="display" style={{ fontSize: 19, margin: "6px 0 2px" }}>📅 Book a free consultation</h3>
      <p className="muted" style={{ fontSize: 12.5, margin: "0 0 14px" }}>Tell us a bit about your project and pick a time that works — {co} will confirm.</p>

      <label className="field-label">Full name</label>
      <input className="input" value={form.name} onChange={e => set("name", e.target.value)} placeholder="Jane Homeowner" autoComplete="name" style={{ marginBottom: 10 }} />
      <div className="pe-2col">
        <div>
          <label className="field-label">Phone</label>
          <input className="input" value={form.phone} onChange={e => set("phone", e.target.value)} placeholder="(208) 555-0100" inputMode="tel" autoComplete="tel" />
        </div>
        <div>
          <label className="field-label">Email</label>
          <input className="input" value={form.email} onChange={e => set("email", e.target.value)} placeholder="jane@email.com" inputMode="email" autoComplete="email" />
        </div>
      </div>
      <label className="field-label" style={{ marginTop: 10 }}>Property address <span className="faint">(optional)</span></label>
      <input className="input" value={form.address} onChange={e => set("address", e.target.value)} placeholder="123 Main St" autoComplete="street-address" style={{ marginBottom: 10 }} />

      <label className="field-label">Fence type</label>
      <div className="pe-chips">
        {FENCE_TYPES.map(t => (
          <button key={t} type="button" className={"pe-chip" + (form.fenceType === t ? " on" : "")} style={form.fenceType === t ? { borderColor: accent, color: accent } : null} onClick={() => set("fenceType", t)}>{t}</button>
        ))}
      </div>
      <label className="field-label" style={{ marginTop: 10 }}>Approximate length <span className="faint">(linear feet, optional)</span></label>
      <input className="input tnum" type="number" min="0" value={form.feet} onChange={e => set("feet", e.target.value)} placeholder="e.g. 150" style={{ marginBottom: 10 }} />

      <label className="field-label">Preferred date</label>
      <input className="input" type="date" min={minDate} value={form.date} onChange={e => set("date", e.target.value)} style={{ marginBottom: 10 }} />
      <label className="field-label">Preferred time window</label>
      <div className="pe-chips">
        {WINDOWS.map(t => (
          <button key={t} type="button" className={"pe-chip" + (form.window === t ? " on" : "")} style={form.window === t ? { borderColor: accent, color: accent } : null} onClick={() => set("window", t)}>{t}</button>
        ))}
      </div>

      <label className="field-label" style={{ marginTop: 10 }}>Anything else? <span className="faint">(optional)</span></label>
      <textarea className="input" rows={3} value={form.notes} onChange={e => set("notes", e.target.value)} placeholder="Gate count, slope, removal of an old fence, HOA rules…" style={{ resize: "vertical" }} />

      <SmsConsent checked={form.smsConsent} onToggle={() => set("smsConsent", !form.smsConsent)} co={co} accent={accent} />
      <button className="pubest-btn" style={{ background: accent }} disabled={!canSubmit} onClick={submit}>{submitting ? "Sending…" : "Request my consultation →"}</button>
      {sendErr && <LeadSendError onRetry={submit} sending={submitting} accent={accent} />}
      <p className="faint" style={{ fontSize: 10.5, marginTop: 10, lineHeight: 1.5 }}>This sends a request — {co} will confirm the exact time. A name plus a phone or email is all we need to reach you.</p>
    </div>
  );
}

/* ---- floating launcher: an Intercom-style corner bubble that expands the
   widget. The contractor's site can render the widget either inline (the iframe
   embed) or floating (this). When the widget is served at /widget/:token with
   ?floating=1, PublicRoute renders this so the launcher snippet's iframe shows
   ONLY the bubble until it's opened. ---- */
function FloatingWidget({ settings, accent = "#19e3c2", headline, preview, store, requireContact, widgetToken, defaultTab, startOpen, standalone }: any) {
  const [open, setOpen] = React.useState<any>(!!startOpen);
  const close = () => {
    setOpen(false);
    // when living inside the launcher snippet's iframe, tell the host to hide us
    try { if (window.parent && window.parent !== window) window.parent.postMessage("fw-widget-close", "*"); } catch (e) {}
  };
  // Esc inside the iframe mirrors the host page's Esc-to-close (keystrokes land
  // in whichever document has focus, so both sides listen).
  React.useEffect(() => {
    if (!standalone) return;
    const onKey = (e: any) => { if (e.key === "Escape") close(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [standalone]);
  // The standalone /widget/:token?floating=1 page lives inside the launcher
  // snippet's iframe. The snippet renders the bubble on the host page and shows
  // this iframe only while open, so this page is JUST the expanded panel,
  // full-bleed — the host-side frame supplies the rounded corners and shadow.
  // (State survives close/reopen because the host merely hides the iframe.)
  if (standalone) {
    return (
      <div className="widget-floating-page">
        <PublicEstimatorForm settings={settings} accent={accent} headline={headline} preview={preview} store={store}
          requireContact={requireContact} widgetToken={widgetToken} defaultTab={defaultTab} floating onClose={close} />
      </div>
    );
  }
  return (
    <div className="fw-float">
      {open && (
        <div className="fw-float-panel">
          <PublicEstimatorForm settings={settings} accent={accent} headline={headline} preview={preview} store={store}
            requireContact={requireContact} widgetToken={widgetToken} defaultTab={defaultTab} floating onClose={close} />
        </div>
      )}
      <button type="button" className="fw-float-bubble" aria-label={open ? "Close" : "Get a quote"} style={{ background: accent }} onClick={() => (open ? close() : setOpen(true))}>
        {open ? <Icon name="x" size={22} /> : <span className="row gap-8" style={{ alignItems: "center" }}><Icon name="calendar" size={16} />Get a Quote</span>}
      </button>
    </div>
  );
}

Object.assign(window, { EstimatorWidgetSettings, PublicEstimatorForm, BookingForm, FloatingWidget, widgetUrl });

export { widgetUrl, EstimatorWidgetSettings, PublicEstimatorForm, BookingForm, FloatingWidget, widgetPriceRanges, widgetRangeStamp };
